import { DataSource, DataSourceAlert, DataSourceCategory, DataSourceStatus } from '../../types';

class DataSourceEngine {
  private sources: DataSource[] = [
    {
      id: 'DS-001',
      code: 'FX-ECB-DAILY',
      name: 'European Central Bank (ECB) FX Reference Rates',
      category: 'CENTRAL_BANK',
      providerOrg: 'European Central Bank',
      endpointUrl: 'https://api.ecb.europa.eu/fx/v2/daily',
      updateFrequency: 'DAILY',
      reliabilityScore: 99.8,
      latencyMs: 140,
      status: 'ACTIVE',
      contactName: 'ECB Statistical Data Support',
      contactEmail: 'statistics@ecb.europa.eu',
      lastSyncedAt: '2026-09-02 14:15:00',
      errorCount: 0,
      notes: 'Primary benchmark feed for EUR crosses and monetary intelligence.',
    },
    {
      id: 'DS-002',
      code: 'TAX-FR-URSSAF',
      name: 'URSSAF & DGFiP French Social / Tax Ruleset API',
      category: 'TAX_AUTHORITY',
      providerOrg: 'République Française (URSSAF / DGFiP)',
      endpointUrl: 'https://api.urssaf.fr/v3/calculators/cotisations',
      updateFrequency: 'DAILY',
      reliabilityScore: 99.1,
      latencyMs: 210,
      status: 'ACTIVE',
      contactName: 'API Enterprise Support Desk',
      contactEmail: 'support.api@urssaf.fr',
      lastSyncedAt: '2026-09-02 06:00:00',
      errorCount: 0,
      notes: 'Governs all French salary, net pay, and employer contribution calculators.',
    },
    {
      id: 'DS-003',
      code: 'FX-EXCHANGERATE-LIVE',
      name: 'ExchangeRate-API Global Real-Time FX Feed',
      category: 'FX_PROVIDER',
      providerOrg: 'ExchangeRate LLC',
      endpointUrl: 'https://v6.exchangerate-api.com/v6/latest/USD',
      updateFrequency: 'REAL_TIME',
      reliabilityScore: 98.5,
      latencyMs: 85,
      status: 'ACTIVE',
      contactName: 'DevOps Escalation Team',
      contactEmail: 'ops@exchangerate-api.com',
      lastSyncedAt: '2026-09-02 16:20:00',
      errorCount: 1,
      notes: 'Real-time multi-currency pair updates for YamooPay Transfer & Wealth modules.',
    },
    {
      id: 'DS-004',
      code: 'TAX-UK-HMRC',
      name: 'HMRC PAYE & National Insurance Tax Rate Feed',
      category: 'TAX_AUTHORITY',
      providerOrg: 'HM Revenue & Customs (UK)',
      endpointUrl: 'https://api.service.gov.uk/hmrc/paye/rates/2026',
      updateFrequency: 'WEEKLY',
      reliabilityScore: 97.4,
      latencyMs: 320,
      status: 'DEGRADED',
      contactName: 'HMRC Digital Services',
      contactEmail: 'api-support@hmrc.gov.uk',
      lastSyncedAt: '2026-08-28 10:00:00',
      errorCount: 3,
      notes: 'Periodic latency spike observed during peak UK tax filing windows.',
    },
    {
      id: 'DS-005',
      code: 'FIN-PLAID-CORE',
      name: 'Plaid Financial Institution Integration Feed',
      category: 'FINANCIAL_INSTITUTION',
      providerOrg: 'Plaid Inc.',
      endpointUrl: 'https://production.plaid.com/data/v1/accounts',
      updateFrequency: 'REAL_TIME',
      reliabilityScore: 99.5,
      latencyMs: 95,
      status: 'ACTIVE',
      contactName: 'Plaid Partner Success',
      contactEmail: 'partners@plaid.com',
      lastSyncedAt: '2026-09-02 16:25:00',
      errorCount: 0,
      notes: 'Account aggregation and institutional balance verification.',
    },
    {
      id: 'DS-006',
      code: 'COMP-FATF-WATCH',
      name: 'FATF Global Sanctions & PEP Compliance Feed',
      category: 'COMPLIANCE_FEED',
      providerOrg: 'Financial Action Task Force',
      endpointUrl: 'https://api.fatf-gafi.org/watchlists/v2',
      updateFrequency: 'HOURLY',
      reliabilityScore: 99.9,
      latencyMs: 180,
      status: 'ACTIVE',
      contactName: 'Compliance Data Operations',
      contactEmail: 'watchlist@fatf-gafi.org',
      lastSyncedAt: '2026-09-02 15:00:00',
      errorCount: 0,
      notes: 'Mandatory AML / KYC compliance screening for international money transfers.',
    },
  ];

  private alerts: DataSourceAlert[] = [
    {
      id: 'ALT-001',
      sourceId: 'DS-004',
      sourceName: 'HMRC PAYE & National Insurance Tax Rate Feed',
      severity: 'MODERATE',
      message: 'Latency exceeded 300ms threshold for 3 consecutive poll cycles.',
      detectedAt: '2026-09-02 12:10:00',
      status: 'INVESTIGATING',
    },
    {
      id: 'ALT-002',
      sourceId: 'DS-003',
      sourceName: 'ExchangeRate-API Global Real-Time FX Feed',
      severity: 'LOW',
      message: 'Transient 503 gateway error detected and automatically retried.',
      detectedAt: '2026-09-02 09:45:00',
      status: 'RESOLVED',
    },
  ];

  private listeners: ((toast: { title: string; message: string; type: 'error' | 'warning' | 'info' }) => void)[] = [];

  public subscribe(listener: (toast: { title: string; message: string; type: 'error' | 'warning' | 'info' }) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify(title: string, message: string, type: 'error' | 'warning' | 'info') {
    this.listeners.forEach((l) => l({ title, message, type }));
  }

  public getSources(): DataSource[] {
    return this.sources;
  }

  public getAlerts(): DataSourceAlert[] {
    return this.alerts;
  }

  public addSource(input: Omit<DataSource, 'id' | 'lastSyncedAt' | 'errorCount'>): DataSource {
    const newSource: DataSource = {
      ...input,
      id: `DS-${String(this.sources.length + 1).padStart(3, '0')}`,
      lastSyncedAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
      errorCount: 0,
    };
    this.sources.unshift(newSource);
    this.notify('Data Source Added', `New source ${newSource.code} registered successfully.`, 'info');
    return newSource;
  }

  public updateSource(id: string, updates: Partial<DataSource>): DataSource {
    const src = this.sources.find((s) => s.id === id);
    if (!src) throw new Error(`Data source ${id} not found.`);
    Object.assign(src, updates);
    if (updates.status === 'FAILING') {
      this.notify('Critical DS Alert', `Data source ${src.code} (${src.name}) is now FAILING!`, 'error');
    } else if (updates.status === 'DEGRADED') {
      this.notify('DS Warning', `Data source ${src.code} status changed to STALE / DEGRADED.`, 'warning');
    }
    return src;
  }

  public setSourceStatus(id: string, status: DataSourceStatus): DataSource {
    const src = this.sources.find((s) => s.id === id);
    if (!src) throw new Error(`Data source ${id} not found.`);
    src.status = status;
    if (status === 'DEACTIVATED' || status === 'FAILING') {
      this.alerts.unshift({
        id: `ALT-${Date.now()}`,
        sourceId: src.id,
        sourceName: src.name,
        severity: status === 'FAILING' ? 'CRITICAL' : 'HIGH',
        message: `Data source status manually updated to ${status}.`,
        detectedAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
        status: 'OPEN',
      });
      this.notify('Data Source Alert', `DataSource ${src.name} status changed to ${status}`, status === 'FAILING' ? 'error' : 'warning');
    }
    return src;
  }

  public testPingSource(id: string): { success: boolean; latencyMs: number; message: string } {
    const src = this.sources.find((s) => s.id === id);
    if (!src) throw new Error(`Data source ${id} not found.`);
    const success = src.status !== 'FAILING' && src.status !== 'DEACTIVATED';
    const latencyMs = Math.floor(Math.random() * 120 + 50);
    src.latencyMs = latencyMs;
    src.lastSyncedAt = new Date().toISOString().replace('T', ' ').substring(0, 19);
    if (success) {
      src.errorCount = 0;
      if (src.status === 'DEGRADED') src.status = 'ACTIVE';
    } else {
      src.errorCount += 1;
      src.status = 'FAILING';
      this.notify('Data Source Failing', `Ping test failed for ${src.name}. Status set to FAILING.`, 'error');
    }
    return {
      success,
      latencyMs,
      message: success ? `Successfully pinged ${src.endpointUrl} (HTTP 200 OK)` : `Ping failed for ${src.name} (Endpoint offline)`,
    };
  }

  public syncWithBackendOnReconnect(): { syncedCount: number; status: string } {
    this.sources = this.sources.map((src) => {
      if (src.status === 'FAILING' || src.status === 'DEGRADED') {
        const recovered = Math.random() > 0.3;
        if (recovered) {
          this.notify('Backend Sync & Recovery', `Data source ${src.name} successfully re-synced with backend. Reliability updated.`, 'info');
          return {
            ...src,
            reliabilityScore: Math.min(99.9, src.reliabilityScore + 0.4),
            lastSyncedAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
            errorCount: 0,
            status: 'ACTIVE',
          };
        }
      }
      return src;
    });
    return {
      syncedCount: this.sources.length,
      status: 'SYNCHRONIZED',
    };
  }

  public resolveAlert(alertId: string): void {
    const alt = this.alerts.find((a) => a.id === alertId);
    if (alt) alt.status = 'RESOLVED';
  }
}

export const dataSourceEngine = new DataSourceEngine();
